/**
 * /earthworks/rates/new and /earthworks/rates/:id — one rate on the
 * earthworks rate card. Full pages, no dialogs for the form; the delete asks
 * in-app first. Admin / office staff write; dispatch roles read.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import {
  useCreateEwRate,
  useDeleteEwRate,
  useEwCustomers,
  useEwEquipmentOptions,
  useEwEquipmentTypes,
  useEwMeta,
  useEwRate,
  useUpdateEwRate,
} from '../../hooks/useEarthworks';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWriteEwRates, catalogLabel, ewDay, ewRateText, ewRefusal } from '../../lib/earthworks';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { isNotFound, EW_EYEBROW, EW_PRIMARY, EW_SECONDARY, EwDialog, EwEmpty, EwFormError, EwLoading, EwPanel, EwSpec } from './EwBits';
import { EwRateForm, type EwRateFormOptions } from './RateForm';
import { ewRateAppliesTo } from './RatesScreen';

function useRateFormOptions(enabled: boolean): EwRateFormOptions {
  const customers = useEwCustomers(enabled).data ?? [];
  const equipment = useEwEquipmentOptions(enabled).data ?? [];
  const equipmentTypes = useEwEquipmentTypes(enabled).data ?? [];
  return { customers, equipment, equipmentTypes };
}

export function EwNewRateScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/earthworks/rates');
  const { user } = useAuth();
  const canWrite = canWriteEwRates(user?.role);
  const metaQuery = useEwMeta();
  const options = useRateFormOptions(canWrite);
  const create = useCreateEwRate();
  const [error, setError] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(metaQuery);
  return (
    <div data-testid="ew-rate-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={EW_EYEBROW} title="New rate" description="One line on the rate card." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          <EwPanel eyebrow="Rate card" title="New rate">
            {!canWrite ? (
              <EwEmpty>Only an admin or office staff can add a rate.</EwEmpty>
            ) : metaQuery.isLoading ? (
              <EwLoading label="Loading form" />
            ) : metaFailure ? (
              <LoadFailureNotice error={queryFailureError(metaQuery)} kind={metaFailure} subject="the rate form" />
            ) : (
              <EwRateForm
                catalog={metaQuery.data?.data ?? null}
                meta={metaQuery.data?.meta ?? null}
                options={options}
                defaultCustomerId={searchParams.get('customerId') ?? ''}
                pending={create.isPending}
                error={error}
                submitLabel="Add rate"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (r) => {
                      toast.success('Rate added');
                      navigate(r.data ? `/earthworks/rates/${r.data.id}` : '/earthworks/rates', { replace: true });
                    },
                    onError: (e) => setError(ewRefusal(e, 'The rate could not be added.')),
                  });
                }}
              />
            )}
          </EwPanel>
        </div>
      </section>
    </div>
  );
}

export default function EwRateDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/earthworks/rates');
  const { user } = useAuth();
  const canWrite = canWriteEwRates(user?.role);
  const query = useEwRate(id);
  const metaQuery = useEwMeta();
  const catalog = metaQuery.data?.data ?? null;
  const update = useUpdateEwRate();
  const del = useDeleteEwRate();
  const [editing, setEditing] = useState(false);
  const options = useRateFormOptions(canWrite && editing);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const r = query.data?.data ?? null;
  const meta = query.data?.meta ?? metaQuery.data?.meta ?? null;
  const failure = queryLoadFailure(query);

  return (
    <div data-testid="ew-rate-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title={r?.code || (query.isLoading ? 'Rate' : '—')}
        description={r ? `${r.name} · ${r.customerId ? r.customerName ?? '—' : 'Default card'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          r && canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <EwLoading label="Loading rate" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !r) ? (
            <EwPanel eyebrow="Rate card" title="Rate">
              <EwEmpty>This rate was not found. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : failure || !r ? (
            <EwPanel eyebrow="Rate card" title="Rate">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this rate" />
            </EwPanel>
          ) : editing && canWrite ? (
            <EwPanel eyebrow="Edit" title={`Edit ${r.code}`} testId="ew-rate-edit">
              <EwRateForm
                rate={r}
                catalog={catalog}
                meta={meta}
                options={options}
                pending={update.isPending}
                error={editError}
                submitLabel="Save rate"
                onCancel={() => setEditing(false)}
                onSubmit={(body) => {
                  setEditError(null);
                  update.mutate(
                    { id: r.id, body },
                    {
                      onSuccess: () => {
                        toast.success('Rate saved');
                        setEditing(false);
                      },
                      onError: (e) => setEditError(ewRefusal(e, 'The rate could not be saved.')),
                    },
                  );
                }}
              />
            </EwPanel>
          ) : (
            <EwPanel eyebrow="Rate card" title="Details" testId="ew-rate-details">
              <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                <EwSpec label="Code" value={r.code || '—'} mono />
                <EwSpec label="Name" value={r.name || '—'} />
                <EwSpec label="Card" value={r.customerId ? r.customerName || '—' : 'Default card'} />
                <EwSpec label="Kind" value={r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} />
                <EwSpec label="Rate" value={ewRateText(r.rate, r.currency, r.unitLabel || catalogLabel(catalog?.priceUnits, r.unit))} mono />
                <EwSpec label="Currency" value={r.currency || '—'} />
                <EwSpec label="Passthrough" value={r.passthrough ? 'Yes' : 'No'} />
                {r.kind === 'equipment' && <EwSpec label="Includes operator" value={r.includesOperator ? 'Yes — the operator is not billed again as labour' : 'No'} />}
                <EwSpec label="Markup" value={r.markupPct != null ? `${r.markupPct}%` : '—'} />
                <EwSpec label="Applies to" value={ewRateAppliesTo(r, catalog?.crewRoles)} />
                <EwSpec label="Starts" value={ewDay(r.effectiveFrom)} />
                <EwSpec label="Ends" value={r.effectiveTo ? ewDay(r.effectiveTo) : 'No end'} />
              </dl>
              {(r.description || r.notes) && (
                <div className="space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                  {r.description && <p>{r.description}</p>}
                  {r.notes && <p className="text-fieldiq-muted">{r.notes}</p>}
                </div>
              )}
            </EwPanel>
          )}
        </div>
      </section>

      {r && canWrite && (
        <EwDialog
          open={deleting}
          title="Delete rate"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={EW_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep it
              </button>
              <button
                type="button"
                className={EW_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(r.id, {
                    onSuccess: () => {
                      toast.success('Rate deleted');
                      navigate('/earthworks/rates', { replace: true });
                    },
                    onError: (e) => setDeleteError(ewRefusal(e, 'The rate could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete rate'}
              </button>
            </>
          }
        >
          <EwFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Removes {r.code} from the rate card.</p>
        </EwDialog>
      )}
    </div>
  );
}
